import { useState, useRef, useEffect } from "react";
import WizardShell from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtEur, loanMonthly } from "@/lib/format";
import { Upload, Sparkles } from "lucide-react";
import { Combobox } from "@/components/ui/combobox";
import { DateField } from "@/components/ui/date-field";
import useHiddenModules, { contractKinds } from "@/lib/useHiddenModules";

const SIGNABLE = /\.(pdf|docx)$/i;

const emptyC = { kind: "huur", start_date: new Date().toISOString().slice(0, 10), end_date: "", monthly_rent: "", penalty_per_day: "", max_penalty_per_month: "", payment_day: 1, indefinite: true, total_amount: "", term_months: "", interest_rate: "", fee_rate: "", due_date: "", invoice_number: "", bank_account_id: "", file_url: "", file_name: "", title: "", auto_renew: false, document_only: false, sign_now: false, sign_email: "" };

export default function ContractWizard({ open, onOpenChange, tenantId, tenant, onSaved }) {
  const [step, setStep] = useState(0);
  const [c, setC] = useState(emptyC);
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [banks, setBanks] = useState([]);
  const fileRef = useRef(null);
  const docRef = useRef(null);
  const set = (k, v) => setC((f) => ({ ...f, [k]: v }));
  const hidden = useHiddenModules();
  const kinds = contractKinds(hidden);
  const onlyOther = kinds.length === 1;
  const isDoc = c.document_only || c.kind === "overig";
  const canSign = SIGNABLE.test(c.file_name || "");

  useEffect(() => { if (open) api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => {}); }, [open]);
  useEffect(() => {
    if (open && hidden.loaded && !kinds.some(([k]) => k === c.kind)) set("kind", kinds[0][0]);
  }, [open, hidden, kinds, c.kind]);
  useEffect(() => {
    if (open) set("sign_email", [...new Set([tenant?.email1, tenant?.email2, tenant?.partner_email].filter((e) => e && e.includes("@")))].join(", "));
  }, [open, tenant]);

  // Prefill factuur-fee en rentepercentage met de standaard van deze debiteur
  useEffect(() => {
    if (open && tenant) setC((f) => ({ ...f, fee_rate: tenant.invoice_fee_rate || "", interest_rate: tenant.default_interest_rate || "" }));
  }, [open, tenant]);

  const invAmount = Number(c.monthly_rent) || 0;
  const feeRate = Number(c.fee_rate) || 0;
  const payout = Math.round((invAmount - invAmount * feeRate / 100) * 100) / 100;

  const parseFile = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/factoring/ai-parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      setC((f) => ({ ...f, monthly_rent: d.amount || f.monthly_rent, start_date: d.invoice_date || f.start_date, due_date: d.due_date || f.due_date, invoice_number: d.invoice_number || f.invoice_number }));
      // Bewaar ook de kopie-factuur zelf zodat die later te bekijken is
      try {
        const fd2 = new FormData();
        fd2.append("file", file);
        const up = await api.post("/upload", fd2, { headers: { "Content-Type": "multipart/form-data" } });
        setC((f) => ({ ...f, file_url: up.data.url }));
      } catch { /* upload optioneel */ }
      toast.success("Factuur ingelezen met AI");
    } catch (e) { toast.error(e?.response?.data?.detail || "Inlezen mislukt"); }
    finally { setParsing(false); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC((f) => ({ ...f, file_url: up.data.url, file_name: file.name, title: f.title || file.name.replace(/\.[^.]+$/, "") }));
      toast.success("Document geüpload");
    } catch (e) { toast.error(e?.response?.data?.detail || "Uploaden mislukt"); }
    finally { setUploading(false); }
  };

  const finish = async () => {
    setLoading(true);
    try {
      if (isDoc) {
        const signing = c.sign_now && canSign;
        const a = signing ? (await api.post("/agreements/custom", { party_type: "debtor", party_id: tenantId, title: c.title || c.file_name, source: "upload", file_url: c.file_url, file_name: c.file_name, extra_signers: [] })).data : null;
        await api.post("/contracts", {
          tenant_id: tenantId, kind: c.kind, title: c.title || "", start_date: c.start_date,
          end_date: c.auto_renew ? "" : c.end_date, auto_renew: !!c.auto_renew,
          document_only: true, file_url: c.file_url || "",
        });
        if (a) {
          const emails = c.sign_email.split(/[,;\s]+/).filter((e) => e.includes("@"));
          try {
            const r = await api.post(`/agreements/${a.id}/send`, { emails, split: true });
            toast.success(`Contract opgeslagen. Elke ondertekenaar krijgt een eigen link: ${(r.data.recipients || []).join(", ")}`);
          } catch (e) { toast.warning(`Contract opgeslagen, maar versturen mislukt: ${e?.response?.data?.detail || "probeer opnieuw via Contracten"}`); }
        } else {
          toast.success("Contract opgeslagen");
        }
      } else if (c.kind === "factuur") {
        const debtor = `${tenant?.first_name || ""} ${tenant?.last_name || ""}`.trim();
        const res = await api.post("/factoring", {
          tenant_id: tenantId, client_id: tenant?.client_id || "", invoice_number: c.invoice_number,
          debtor_name: debtor, invoice_date: c.start_date, due_date: c.due_date,
          amount: invAmount, interest_rate: feeRate, iban: tenant?.iban || "", file_url: c.file_url || "",
        });
        toast.success(`Factuur toegevoegd — uit te betalen ${fmtEur(res.data?.payout ?? payout)}. Klaargezet in Uitbetalingen.`);
      } else {
        const monthly = c.kind === "lening" ? Number(loanMonthly(c.total_amount, c.interest_rate, c.term_months).toFixed(2)) : Number(c.monthly_rent) || 0;
        await api.post("/contracts", {
          tenant_id: tenantId, kind: c.kind, start_date: c.start_date, end_date: c.kind === "huur" && !c.indefinite ? c.end_date : "", monthly_rent: monthly,
          penalty_per_day: Number(c.penalty_per_day) || 0, max_penalty_per_month: Number(c.max_penalty_per_month) || 0,
          payment_day: Number(c.payment_day) || 1, indefinite: c.kind === "huur" ? !!c.indefinite : false,
          total_amount: Number(c.total_amount) || 0, term_months: Number(c.term_months) || 0, interest_rate: Number(c.interest_rate) || 0,
          bank_account_id: c.bank_account_id || "",
        });
        toast.success("Overeenkomst toegevoegd");
      }
      onSaved?.();
      onOpenChange(false); setStep(0); setC(emptyC);
    } catch (e) { toast.error(e?.response?.data?.detail || "Toevoegen mislukt"); }
    finally { setLoading(false); }
  };

  const canFinish = isDoc ? (!!c.start_date && !!c.file_url && (c.auto_renew || !!c.end_date) && (!c.sign_now || c.sign_email.includes("@"))) : c.kind === "lening" ? (c.total_amount && c.term_months) : c.kind === "factuur" ? invAmount > 0 : (c.monthly_rent && (c.indefinite || c.end_date));
  const steps = isDoc ? ["Contract uploaden"] : c.kind === "factuur" ? ["Factuur & fee", "Uitbetaling bevestigen"] : ["Type & bedrag", "Boete & voorwaarden"];

  return (
    <WizardShell open={open} onOpenChange={onOpenChange}
      title="Overeenkomst toevoegen"
      subtitle={onlyOther ? "Contract voor deze debiteur" : `${kinds.map(([, l]) => l).join(", ")} voor deze debiteur`}
      steps={steps}
      step={step} setStep={setStep} onFinish={finish} loading={loading}
      canNext={step !== steps.length - 1 || !!canFinish}
      finishLabel={isDoc ? (c.sign_now && canSign ? "Opslaan & laten ondertekenen" : "Contract opslaan") : c.kind === "factuur" ? "Factuur opslaan & uitbetaling klaarzetten" : "Overeenkomst opslaan"}>
      {step === 0 && (
        <div className="space-y-4">
          {!onlyOther && <div>
            <Label>Type overeenkomst</Label>
            <div className={`grid gap-2 mt-2 ${kinds.length === 4 ? "grid-cols-2 sm:grid-cols-4" : kinds.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
              {kinds.map(([v, l]) => (
                <button key={v} type="button" data-testid={`cw-kind-${v}`} onClick={() => set("kind", v)}
                  className={`border rounded-lg p-3 text-sm font-medium ${c.kind === v ? "ring-2 ring-[#C9A227]/50 border-[#0F1E3D] bg-[#0F1E3D]/5 text-[#0F1E3D]" : "border-slate-200 text-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>}

          {c.kind !== "overig" && <label className="flex items-start gap-2 text-sm cursor-pointer border rounded-lg p-3 bg-slate-50/60" data-testid="cw-document-only-toggle">
            <input type="checkbox" data-testid="cw-document-only" checked={c.document_only} onChange={(e) => set("document_only", e.target.checked)} className="mt-0.5" />
            <span><b>Alleen contract-document uploaden</b><br /><span className="text-xs text-slate-500">Sla enkel het contract op met in- en einddatum. Er worden geen termijnen, uitbetalingen of leningsovereenkomsten aangemaakt.</span></span>
          </label>}


          {isDoc ? (
            <div className="space-y-4">
              {c.kind === "overig" && <div><Label>Omschrijving contract</Label><Input data-testid="cw-doc-title-input" value={c.title} onChange={(e) => set("title", e.target.value)} placeholder="bijv. Betalingsregeling, Gebruiksovereenkomst…" /></div>}
              <div>
                <Label>Contractdocument (Word, PDF of foto)</Label>
                <input ref={docRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" className="hidden" data-testid="cw-doc-file-input" onChange={(e) => uploadDoc(e.target.files?.[0])} />
                <Button type="button" onClick={() => docRef.current?.click()} disabled={uploading} variant="outline" className="w-full mt-2" data-testid="cw-doc-upload-button">
                  {uploading ? <><Sparkles className="h-4 w-4 mr-2 animate-pulse" /> Uploaden…</> : <><Upload className="h-4 w-4 mr-2" /> {c.file_url ? "Document vervangen" : "Document uploaden"}</>}
                </Button>
                {c.file_url && <p className="text-xs text-emerald-600 mt-1" data-testid="cw-doc-uploaded">Document geüpload ✓ {c.file_name}</p>}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Ingangsdatum</Label><DateField testId="cw-doc-start-input" value={c.start_date} onChange={(v) => set("start_date", v)} /></div>
                {!c.auto_renew && <div><Label>Einddatum <span className="text-red-500">*</span></Label><DateField testId="cw-doc-end-input" value={c.end_date} onChange={(v) => set("end_date", v)} min={c.start_date} /></div>}
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="cw-doc-autorenew" checked={c.auto_renew} onChange={(e) => set("auto_renew", e.target.checked)} /> Automatisch verlengd (geen vaste einddatum)
              </label>
              <SignNow c={c} set={set} canSign={canSign} />
            </div>
          ) : (
          <>
          {c.kind === "factuur" && (
            <div className="space-y-3">
              <div>
                <Label>Upload de factuur (PDF of foto) — AI leest de gegevens in</Label>
                <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" data-testid="cw-factuur-file-input" onChange={(e) => parseFile(e.target.files?.[0])} />
                <Button type="button" onClick={() => fileRef.current?.click()} disabled={parsing} variant="outline" className="w-full mt-2" data-testid="cw-factuur-upload-button">
                  {parsing ? <><Sparkles className="h-4 w-4 mr-2 animate-pulse" /> AI leest factuur...</> : <><Upload className="h-4 w-4 mr-2" /> Factuur uploaden & inlezen</>}
                </Button>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>{c.kind === "factuur" ? "Factuurdatum" : "Ingangsdatum"}</Label><DateField testId="cw-start-input" value={c.start_date} onChange={(v) => set("start_date", v)} /></div>
            {c.kind === "lening" ? (
              <div><Label>Leenbedrag (€)</Label><Input type="number" data-testid="cw-total-input" value={c.total_amount} onChange={(e) => set("total_amount", e.target.value)} /></div>
            ) : (
              <div><Label>{c.kind === "factuur" ? "Factuurbedrag (€)" : "Maandhuur (€)"}</Label><Input type="number" data-testid="cw-rent-input" value={c.monthly_rent} onChange={(e) => set("monthly_rent", e.target.value)} /></div>
            )}
            {c.kind === "factuur" && <>
              <div><Label>Vervaldatum</Label><DateField testId="cw-duedate-input" value={c.due_date} onChange={(v) => set("due_date", v)} /></div>
              <div><Label>Rente / fee (%)</Label><Input type="number" data-testid="cw-fee-input" value={c.fee_rate} onChange={(e) => set("fee_rate", e.target.value)} placeholder={tenant?.invoice_fee_rate ? String(tenant.invoice_fee_rate) : "0"} /></div>
            </>}
            {c.kind === "lening" && <>
              <div><Label>Looptijd (mnd)</Label><Input type="number" data-testid="cw-term-input" value={c.term_months} onChange={(e) => set("term_months", e.target.value)} /></div>
              <div><Label>Rente per jaar (%)</Label><Input type="number" data-testid="cw-interest-input" value={c.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} /></div>
            </>}
          </div>

          {c.kind === "factuur" && invAmount > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 grid grid-cols-3 gap-3 text-center" data-testid="cw-payout-preview">
              <div><div className="text-xs text-slate-500">Factuurbedrag</div><div className="font-bold text-slate-800">{fmtEur(invAmount)}</div></div>
              <div><div className="text-xs text-slate-500">Fee ({feeRate}%)</div><div className="font-bold text-red-600">-{fmtEur(invAmount * feeRate / 100)}</div></div>
              <div><div className="text-xs text-slate-500">Uit te betalen</div><div className="font-bold text-emerald-700">{fmtEur(payout)}</div></div>
            </div>
          )}
          {c.kind === "lening" && c.total_amount && c.term_months && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">Maandbedrag (annuïteit): <b className="text-emerald-700">{fmtEur(loanMonthly(c.total_amount, c.interest_rate, c.term_months))}</b></div>
          )}
          {c.kind === "huur" && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="cw-indefinite" checked={c.indefinite} onChange={(e) => set("indefinite", e.target.checked)} /> Onbepaalde tijd (12 mnd rollend)
              </label>
              {!c.indefinite && (
                <div><Label>Einddatum <span className="text-red-500">*</span></Label><DateField testId="cw-enddate-input" value={c.end_date} onChange={(v) => set("end_date", v)} min={c.start_date} /><p className="text-xs text-slate-400 mt-1">Termijnen worden aangemaakt tot en met de maand van de einddatum.</p></div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      )}
      {step === 1 && c.kind === "factuur" && (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="cw-payout-confirm">
            <div className="text-sm text-slate-500 mb-1">Uit te betalen bedrag</div>
            <div className="text-3xl font-bold text-emerald-700">{fmtEur(payout)}</div>
            <div className="text-xs text-slate-500 mt-2">Factuur {fmtEur(invAmount)} − fee {feeRate}% ({fmtEur(invAmount * feeRate / 100)})</div>
          </div>
          <div className="text-sm text-slate-600 space-y-1 border rounded-lg p-4">
            <div className="flex justify-between"><span className="text-slate-500">Begunstigde</span><span className="font-medium">{`${tenant?.first_name || ""} ${tenant?.last_name || ""}`.trim() || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">IBAN</span><span className="font-mono">{tenant?.iban || "—"}</span></div>
          </div>
          <p className="text-xs text-slate-400 text-center">Deze uitbetaling wordt klaargezet in het menu <b>Uitbetalingen</b> en kan daar handmatig worden afgevinkt.</p>
        </div>
      )}
      {step === 1 && c.kind !== "factuur" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Boete per dag (€)</Label><Input type="number" data-testid="cw-penalty-input" value={c.penalty_per_day} onChange={(e) => set("penalty_per_day", e.target.value)} /></div>
          <div><Label>Max. boete per maand (€)</Label><Input type="number" data-testid="cw-maxpenalty-input" value={c.max_penalty_per_month} onChange={(e) => set("max_penalty_per_month", e.target.value)} /></div>
          <div><Label>Betaaldag (dag van maand)</Label><Input type="number" min={1} max={28} value={c.payment_day} onChange={(e) => set("payment_day", e.target.value)} /></div>
          <div className="sm:col-span-2">
            <Label>Rekeningnummer voor betaling</Label>
            <div className="mt-1"><Combobox testId="cw-bank-select" value={c.bank_account_id} onChange={(v) => set("bank_account_id", v)} searchPlaceholder="Zoek rekening…"
              options={[{ value: "", label: "— Debiteur-IBAN gebruiken (standaard) —" }, ...banks.map((b) => ({ value: b.id, label: b.name_tag ? `${b.name_tag} · ${b.iban}` : b.iban }))]} /></div>
            <p className="text-xs text-slate-400 mt-1">Deze rekening wordt in de herinneringen vermeld als betaalrekening. Beheer rekeningen bij Instellingen → Bankrekeningen.</p>
          </div>
        </div>
      )}
    </WizardShell>
  );
}

function SignNow({ c, set, canSign }) {
  return (
    <div className={`border rounded-lg p-3 space-y-2 ${canSign ? "border-[#C9A227]/50 bg-[#C9A227]/5" : "bg-slate-50/60"}`} data-testid="cw-sign-now-box">
      <label className={`flex items-start gap-2 text-sm ${canSign ? "cursor-pointer" : "opacity-60"}`}>
        <input type="checkbox" data-testid="cw-sign-now" disabled={!canSign} checked={c.sign_now && canSign} onChange={(e) => set("sign_now", e.target.checked)} className="mt-0.5" />
        <span><b>Direct laten ondertekenen</b><br /><span className="text-xs text-slate-500">{canSign ? "Elk e-mailadres wordt een aparte ondertekenaar met een eigen link; iedereen parafeert en tekent zelf." : "Mogelijk bij een Word- (.docx) of PDF-document."}</span></span>
      </label>
      {c.sign_now && canSign && <div><Label className="text-xs">E-mailadressen ondertekenaar (gescheiden door komma)</Label><Input data-testid="cw-sign-email" value={c.sign_email} onChange={(e) => set("sign_email", e.target.value)} placeholder="naam@voorbeeld.nl" /></div>}
    </div>
  );
}
